import { useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { toast } from "sonner"

import { directUpload } from "@/lib/directUpload"
import { csrfToken } from "@/lib/orderActions"

export const REQUIREMENTS_UPLOAD_COPY = {
  upload: "Upload file",
  uploading: "Uploading…",
  helper: "Upload one file: Word (.doc or .docx), PDF or ZIP.",
  started: "Your outline is uploaded, and your manuscript is now in the queue for a writer.",
  saved: "Your file has been saved.",
  wrongType: "This file type isn't supported. Choose a Word (.doc or .docx), PDF or ZIP file.",
}

// The types OrderActions::RequirementsUploaded accepts.
export const REQUIREMENTS_ACCEPT = ".doc,.docx,.pdf,.zip"
const ACCEPTED_NAME = /\.(docx?|pdf|zip)$/i

// The Order Details requirements-file upload: the one write on the tab. For a
// book On Hold until its outline arrives, this upload is what starts the
// order: the server moves it to New and answers `started`, and the reload
// brings back the new phase.
export function useRequirementsUpload(orderId) {
  const inputRef = useRef(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  // The file goes straight to storage first, then the endpoint is handed its
  // signed id — the Shared Files upload's transport, for the same reason.
  async function onChange(event) {
    const chosen = event.target.files?.[0]
    event.target.value = ""
    if (!chosen) return

    if (!ACCEPTED_NAME.test(chosen.name)) {
      setError(REQUIREMENTS_UPLOAD_COPY.wrongType)
      return
    }

    setSaving(true)
    setError("")

    try {
      const body = new FormData()
      body.append("file", await directUpload(chosen))

      const response = await fetch(`/projects/${orderId}/requirements`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
        body,
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload.error || "")

      toast.success(payload.started ? REQUIREMENTS_UPLOAD_COPY.started : REQUIREMENTS_UPLOAD_COPY.saved)
      router.reload({ only: ["order"] })
    } catch (uploadError) {
      setError(uploadError.message || "We couldn't upload that file. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  return { inputRef, onChange, saving, error, choose: () => inputRef.current?.click() }
}
